import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { ReportsState, UseReportsReturn } from "./type";
import * as socialAPI from "../../Data/Social";
import { ReportType, CreateReportParams, UpdateReportParams } from "../../Data/Social/types";
import { contentFilter } from "../../Data/Social/ContentFilter";

const validateReport = (report: any): report is ReportType => {
    return report && 
           typeof report.addressid === 'string' && 
           typeof report.target === 'string' && 
           typeof report.reason === 'string' && 
           typeof report.status === 'string';
};

// Reports state atom
export const reportsState = atom<ReportsState>({
    key: "reportsState" + randomString(4),
    default: {
        items: {},
        myReports: [],
        pendingReports: [],
        loading: false,
        error: null
    }
});

export const useReports = (): UseReportsReturn => {
    const [state, setState] = useRecoilState(reportsState);
    const resetReports = useResetRecoilState(reportsState);

    // Create a new report
    const createReport = useCallback(async (params: CreateReportParams): Promise<ReportType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.createReport(params);
            
            if (result && validateReport(result)) {
                const reportId = `${result.addressid}-${result.target}`;
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [reportId]: result },
                    myReports: [result, ...prev.myReports],
                    loading: false
                }));
                
                // Add reported content to content filter to hide it
                try {
                    if (params.target.startsWith('@')) {
                        await contentFilter.addReportedUser(params.target.slice(1));
                    } else if (params.target.startsWith('#')) {
                        await contentFilter.addReportedHashtag(params.target.slice(1));
                    } else /* 'post' */ {
                        await contentFilter.addReportedPost(params.target);
                    }
                } catch (filterError) {
                    console.error('Failed to add content to filter:', filterError);
                    // Don't fail the report creation if filtering fails
                }
                
                return result;
            } else {
                setState(prev => ({ ...prev, error: "Failed to create report", loading: false }));
                return null;
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to create report",
                loading: false
            }));
            console.error('Failed to create report:', error);
            return null;
        }
    }, [setState]);

    // Get all reports (admin only)
    const getReports = useCallback(async (): Promise<ReportType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.listReports_basic();
            
            if (result && Array.isArray(result)) {
                const validReports = result.filter(validateReport);
                const items = Object.fromEntries(
                    validReports.map(report => [`${report.addressid}-${report.target}`, report])
                );
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    loading: false
                }));
                
                return validReports;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get reports",
                loading: false
            }));
            console.error('Failed to get reports:', error);
            return [];
        }
    }, [setState]);

    // Get pending reports (admin only)
    const getPendingReports = useCallback(async (): Promise<ReportType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.listPendingReports();
            
            if (result && Array.isArray(result)) {
                const validReports = result.filter(validateReport);
                const items = Object.fromEntries(
                    validReports.map(report => [`${report.addressid}-${report.target}`, report])
                );
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    pendingReports: validReports,
                    loading: false
                }));
                
                return validReports;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get pending reports",
                loading: false
            }));
            console.error('Failed to get pending reports:', error);
            return [];
        }
    }, [setState]);

    // Get my reports
    const getMyReports = useCallback(async (): Promise<ReportType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.listMyReports();
            
            if (result && Array.isArray(result)) {
                const validReports = result.filter(validateReport);
                const items = Object.fromEntries(
                    validReports.map(report => [`${report.addressid}-${report.target}`, report])
                );
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    myReports: validReports,
                    loading: false
                }));
                
                return validReports;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get my reports",
                loading: false
            }));
            console.error('Failed to get my reports:', error);
            return [];
        }
    }, [setState]);

    // Update report status (admin only)
    const updateReportStatus = useCallback(async (params: UpdateReportParams): Promise<ReportType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.updateReportStatus(params);
            
            if (result && validateReport(result)) {
                const reportId = `${result.addressid}-${result.target}`;
                
                setState(prev => {
                    const updatedItems = { ...prev.items, [reportId]: result };
                    
                    // Update pending reports list if status changed
                    const updatedPendingReports = prev.pendingReports.map(report => 
                        report.addressid === result.addressid && report.target === result.target 
                            ? result 
                            : report
                    ).filter(report => report.status === 'pending');
                    
                    return {
                        ...prev,
                        items: updatedItems,
                        pendingReports: updatedPendingReports,
                        loading: false
                    };
                });
                
                return result;
            } else {
                setState(prev => ({ ...prev, error: "Failed to update report status", loading: false }));
                return null;
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to update report status",
                loading: false
            }));
            console.error('Failed to update report status:', error);
            return null;
        }
    }, [setState]);

    // Resolve report (admin only)
    const resolveReport = useCallback(async (addressid: string, target: string): Promise<boolean> => {
        try {
            return await updateReportStatus({ addressid, target, status: 'resolved' }) !== null;
        } catch (error) {
            console.error('Failed to resolve report:', error);
            return false;
        }
    }, [updateReportStatus]);

    // Dismiss report (admin only)
    const dismissReport = useCallback(async (addressid: string, target: string): Promise<boolean> => {
        try {
            return await updateReportStatus({ addressid, target, status: 'dismissed' }) !== null;
        } catch (error) {
            console.error('Failed to dismiss report:', error);
            return false;
        }
    }, [updateReportStatus]);

    return {
        reports: state.items,
        myReports: state.myReports,
        pendingReports: state.pendingReports,
        loading: state.loading,
        error: state.error,
        
        // Report functions
        createReport,
        getReports,
        getPendingReports,
        getMyReports,
        updateReportStatus,
        resolveReport,
        dismissReport,

        reset: resetReports
    };
};